/* Matter Editor — Feb-2026.
 *
 * Author-side front + back matter editor. Also mounted inside the
 * collaborative Workspace so the assigned Editor / QA reviewer can
 * amend the fields before publication.
 *
 * Layout: tabbed panel with two tabs (Front / Back). Each slot is
 * collapsible with an "Include in book" checkbox that gates the body.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, BookMarked, Save } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const ax  = axios.create({ baseURL: API, withCredentials: true });

const FRONT_LABELS = {
  half_title:     { label: 'Half-Title Page',   help: 'The main title only — no author or subtitle.' },
  frontispiece:   { label: 'Frontispiece',      help: 'Optional decorative illustration facing the title page.' },
  title_page:     { label: 'Title Page',        help: 'Full title, optional subtitle, author and publisher.' },
  copyright_page: { label: 'Copyright Page',    help: 'Legal notice, ISBN, publisher, copyright year.' },
  dedication:     { label: 'Dedication',        help: 'A short note to someone special.' },
  epigraph:       { label: 'Epigraph',          help: 'Quote or poem hinting at the theme.' },
  toc:            { label: 'Table of Contents', help: 'Auto-generated from your chapters.' },
  foreword:       { label: 'Foreword / Preface', help: 'Foreword = someone else; Preface = you.' },
};

const BACK_LABELS = {
  epilogue:        { label: 'Epilogue',           group: 'Story Extensions' },
  postscript:      { label: 'Postscript / Appendix', group: 'Story Extensions' },
  afterword:       { label: 'Afterword',          group: 'Author Reflections' },
  authors_note:    { label: "Author's Note",      group: 'Author Reflections' },
  acknowledgments: { label: 'Acknowledgments',    group: 'Gratitude & Community' },
  reading_group:   { label: 'Reading Group Guide', group: 'Gratitude & Community' },
  sneak_peek:      { label: 'Sneak Peek',         group: 'Promotional' },
  also_by:         { label: 'Also by the Author', group: 'Promotional' },
  about_author:    { label: 'About the Author',   group: 'Biographical' },
  colophon:        { label: 'Colophon',           group: 'Biographical' },
};

function TextField({ label, value, onChange, rows = 2, testid }) {
  if (rows > 1) {
    return (
      <div>
        <label className="mb-1 block text-[13px] uppercase tracking-wider text-foreground/70">{label}</label>
        <Textarea rows={rows} value={value || ''}
                  onChange={(e) => onChange(e.target.value)}
                  data-testid={testid}
                  className="text-[14px]" />
      </div>
    );
  }
  return (
    <div>
      <label className="mb-1 block text-[13px] uppercase tracking-wider text-foreground/70">{label}</label>
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)}
             data-testid={testid} className="text-[14px]" />
    </div>
  );
}

function Slot({ id, label, help, children, patch, front, back, setFront, setBack, kind }) {
  const src = kind === 'front' ? front : back;
  const row = src?.[id] || { include: false };
  const setInclude = (v) => {
    const setter = kind === 'front' ? setFront : setBack;
    setter((cur) => ({ ...cur, [id]: { ...(cur[id] || {}), include: v } }));
  };
  return (
    <div className={`rounded-md border p-3 ${row.include ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'}`}
         data-testid={`matter-slot-${kind}-${id}`}>
      <label className="flex items-center gap-2 text-[13px] font-semibold">
        <input type="checkbox" checked={!!row.include}
               onChange={(e) => setInclude(e.target.checked)}
               data-testid={`matter-include-${kind}-${id}`} />
        {label}
      </label>
      {help && <p className="mt-0.5 text-[13px] text-foreground/70">{help}</p>}
      {row.include && <div className="mt-2 space-y-2">{children}</div>}
    </div>
  );
}

export default function MatterEditor({ projectId, onSaved, onClose }) {
  const [front, setFront] = useState(null);
  const [back,  setBack]  = useState(null);
  const [tab,   setTab]   = useState('front');
  const [busy,  setBusy]  = useState(false);

  const load = useCallback(async () => {
    if (!projectId) return;
    setBusy(true);
    try {
      const r = await ax.get(`/projects/${projectId}/matter`);
      setFront(r.data.front_matter);
      setBack(r.data.back_matter);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load matter.');
    } finally { setBusy(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const patch = (kind, id, key, value) => {
    const setter = kind === 'front' ? setFront : setBack;
    setter((cur) => ({ ...cur, [id]: { ...(cur?.[id] || {}), [key]: value } }));
  };

  const save = async () => {
    setBusy(true);
    try {
      const r = await ax.put(`/projects/${projectId}/matter`,
                              { front_matter: front, back_matter: back });
      setFront(r.data.front_matter);
      setBack(r.data.back_matter);
      toast.success('Front + back matter saved.');
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const backGroups = useMemo(() => {
    const groups = {};
    Object.entries(BACK_LABELS).forEach(([id, meta]) => {
      groups[meta.group] = groups[meta.group] || [];
      groups[meta.group].push(id);
    });
    return groups;
  }, []);

  if (busy && !front) {
    return (
      <div className="rounded-md border border-border bg-card p-3 text-[13.5px] text-muted-foreground">
        <Loader2 className="mr-1 inline-block h-3.5 w-3.5 animate-spin" /> Loading matter…
      </div>
    );
  }
  if (!front || !back) return null;

  return (
    <div className="rounded-md border border-border bg-card p-4"
         data-testid="matter-editor">
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2 text-[13px] font-semibold">
          <BookMarked className="h-4 w-4" /> Front &amp; Back Matter
        </div>
        {onClose && (
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"
                  data-testid="matter-close-btn">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="mb-3 flex gap-1 border-b border-border">
        {['front', 'back'].map((t) => (
          <button key={t}
                  onClick={() => setTab(t)}
                  data-testid={`matter-tab-${t}`}
                  className={`rounded-t-md px-3 py-1.5 text-[13.5px] font-semibold uppercase tracking-wider ${
                    tab === t
                      ? 'border border-b-transparent border-border bg-background text-foreground'
                      : 'text-muted-foreground hover:text-foreground'}`}>
            {t} matter
          </button>
        ))}
      </div>

      {tab === 'front' && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {Object.entries(FRONT_LABELS).map(([id, meta]) => (
            <Slot key={id} id={id} label={meta.label} help={meta.help}
                  kind="front" front={front} back={back}
                  setFront={setFront} setBack={setBack}>
              {id === 'title_page' && (
                <>
                  <TextField label="Subtitle" value={front.title_page.subtitle}
                             onChange={(v) => patch('front','title_page','subtitle',v)}
                             testid="matter-title-subtitle" rows={1} />
                  <TextField label="Publisher" value={front.title_page.publisher}
                             onChange={(v) => patch('front','title_page','publisher',v)}
                             testid="matter-title-publisher" rows={1} />
                  <TextField label="Logo URL" value={front.title_page.logo_url}
                             onChange={(v) => patch('front','title_page','logo_url',v)}
                             testid="matter-title-logo" rows={1} />
                </>
              )}
              {id === 'copyright_page' && (
                <>
                  <TextField label="Copyright year" value={front.copyright_page.year || ''}
                             onChange={(v) => patch('front','copyright_page','year', parseInt(v,10)||0)}
                             testid="matter-copyright-year" rows={1} />
                  <TextField label="Rights holder" value={front.copyright_page.holder}
                             onChange={(v) => patch('front','copyright_page','holder',v)}
                             testid="matter-copyright-holder" rows={1} />
                  <TextField label="ISBN" value={front.copyright_page.isbn}
                             onChange={(v) => patch('front','copyright_page','isbn',v)}
                             testid="matter-copyright-isbn" rows={1} />
                  <TextField label="Custom notice (optional)" value={front.copyright_page.notice}
                             onChange={(v) => patch('front','copyright_page','notice',v)}
                             testid="matter-copyright-notice" rows={3} />
                </>
              )}
              {id === 'frontispiece' && (
                <TextField label="Image URL" value={front.frontispiece.image_url}
                           onChange={(v) => patch('front','frontispiece','image_url',v)}
                           testid="matter-frontispiece-url" rows={1} />
              )}
              {id === 'dedication' && (
                <TextField label="Dedication" value={front.dedication.text}
                           onChange={(v) => patch('front','dedication','text',v)}
                           testid="matter-dedication-text" rows={2} />
              )}
              {id === 'epigraph' && (
                <>
                  <TextField label="Quote" value={front.epigraph.quote}
                             onChange={(v) => patch('front','epigraph','quote',v)}
                             testid="matter-epigraph-quote" rows={3} />
                  <TextField label="Attribution" value={front.epigraph.attribution}
                             onChange={(v) => patch('front','epigraph','attribution',v)}
                             testid="matter-epigraph-attribution" rows={1} />
                </>
              )}
              {id === 'foreword' && (
                <>
                  <div>
                    <label className="mb-1 block text-[13px] uppercase tracking-wider text-muted-foreground">Kind</label>
                    <div className="flex gap-1">
                      {['foreword', 'preface'].map((k) => (
                        <button key={k}
                                onClick={() => patch('front','foreword','kind',k)}
                                data-testid={`matter-foreword-kind-${k}`}
                                className={`rounded-full border px-2 py-0.5 text-[13px] font-semibold uppercase ${
                                  front.foreword.kind === k
                                    ? 'border-primary bg-primary text-primary-foreground'
                                    : 'border-border bg-background hover:bg-secondary'}`}>
                          {k}
                        </button>
                      ))}
                    </div>
                  </div>
                  <TextField label="Author (leave blank if you wrote it)"
                             value={front.foreword.author}
                             onChange={(v) => patch('front','foreword','author',v)}
                             testid="matter-foreword-author" rows={1} />
                  <TextField label="Body" value={front.foreword.body}
                             onChange={(v) => patch('front','foreword','body',v)}
                             testid="matter-foreword-body" rows={6} />
                </>
              )}
            </Slot>
          ))}
        </div>
      )}

      {tab === 'back' && (
        <div className="space-y-4">
          {Object.entries(backGroups).map(([group, ids]) => (
            <div key={group}>
              <div className="mb-1 flex items-center gap-2 text-[13px] uppercase tracking-[0.24em] text-muted-foreground">
                {group}
                <Badge variant="outline" className="text-[9px]">{ids.length}</Badge>
              </div>
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {ids.map((id) => {
                  const row = back[id];
                  return (
                    <Slot key={id} id={id} label={BACK_LABELS[id].label} help={null}
                          kind="back" front={front} back={back}
                          setFront={setFront} setBack={setBack}>
                      {id === 'postscript' && (
                        <TextField label="Title" value={row.title}
                                   onChange={(v) => patch('back',id,'title',v)}
                                   testid={`matter-${id}-title`} rows={1} />
                      )}
                      {id === 'sneak_peek' && (
                        <TextField label="Next title" value={row.next_title}
                                   onChange={(v) => patch('back',id,'next_title',v)}
                                   testid={`matter-${id}-next-title`} rows={1} />
                      )}
                      {(id === 'reading_group' || id === 'also_by') && (
                        <TextField
                          label={id === 'reading_group' ? 'Questions (one per line)' : 'Titles (one per line)'}
                          value={(id === 'reading_group' ? row.questions : row.titles || []).join('\n')}
                          onChange={(v) => patch('back', id,
                            id === 'reading_group' ? 'questions' : 'titles',
                            v.split('\n').map((s) => s.trim()).filter(Boolean))}
                          testid={`matter-${id}-list`} rows={4} />
                      )}
                      {id === 'about_author' && (
                        <>
                          <TextField label="Bio" value={row.body}
                                     onChange={(v) => patch('back',id,'body',v)}
                                     testid={`matter-${id}-body`} rows={4} />
                          <TextField label="Photo URL" value={row.photo_url}
                                     onChange={(v) => patch('back',id,'photo_url',v)}
                                     testid={`matter-${id}-photo`} rows={1} />
                          <TextField label="Links (one per line)"
                                     value={(row.links || []).join('\n')}
                                     onChange={(v) => patch('back',id,'links',
                                       v.split('\n').map((s) => s.trim()).filter(Boolean))}
                                     testid={`matter-${id}-links`} rows={2} />
                        </>
                      )}
                      {(id === 'epilogue' || id === 'afterword' || id === 'authors_note'
                          || id === 'acknowledgments' || id === 'colophon'
                          || id === 'postscript' || id === 'sneak_peek') && (
                        <TextField label="Body" value={row.body}
                                   onChange={(v) => patch('back',id,'body',v)}
                                   testid={`matter-${id}-body`} rows={id === 'epilogue' || id === 'sneak_peek' ? 8 : 5} />
                      )}
                    </Slot>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-4 flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onClose}
                data-testid="matter-cancel-btn">
          Cancel
        </Button>
        <Button size="sm" onClick={save} disabled={busy}
                data-testid="matter-save-btn">
          {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                : <Save className="mr-1 h-3.5 w-3.5" />}
          Save matter
        </Button>
      </div>
    </div>
  );
}
